import { useState } from 'react';

import { useAppDispatch } from 'app/hooks';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { UsersWithNewAccessReportEntry,UsersWithNewAccessReportRequest } from 'common/services/api/Reports/UsersWithNewAccess.api';
import { fetchUsersWithNewAccessByAcl, fetchUsersWithNewAccessByUtm } from 'common/services/api/Reports/UsersWithNewAccess.thunk';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

export const useFetchUsersWithNewAccessReport = () => {
    const [processing, setProcessing] = useState<boolean>(false);
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();

    const _handleError = (error: unknown) => {
        const err = error as Error;
        const message = err.message?.toLowerCase() || '';
        if (message.includes('502')) {
            enqueueSnackbar(
                'There was too much data to export. Please reduce the scope of your report (e.g., select fewer resources or a shorter date range) and try again.',
                { variant: 'error' }
            );
        } else {
            const statusCode = extractStatusCode(err.message);
            handleListFetchError(statusCode, 'New Users Access report', enqueueSnackbar);
        }
        setProcessing(false);
    };

    /**
     * @param tenantId string (required)
     * @param request UsersWithNewAccessReportRequest (required)
     * @param reportType 'acl' | 'utm' (required)
     */
    const processFetchNewUsersAccessReportData = async (
        tenantId: string,
        request: UsersWithNewAccessReportRequest,
        reportType: 'acl' | 'utm'
    ): Promise<UsersWithNewAccessReportEntry[]> => {
        setProcessing(true);
        try {
            if (reportType === 'acl') {
                return await dispatch(fetchUsersWithNewAccessByAcl({ tenantId, request })).unwrap();
            } else {
                return await dispatch(fetchUsersWithNewAccessByUtm({ tenantId, request })).unwrap();
            }
        } catch (error) {
            _handleError(error);
            return [];
        } finally {
            setProcessing(false);
        }
    };

    return { processing, processFetchNewUsersAccessReportData };
};
